import type { ReactNode } from 'react';

/**
 * The four marks on the roadmap cards.
 *
 * Drawn rather than imported. @huaca/ui ships icons for the application shell, and a set built for a
 * toolbar reads as a toolbar wherever it lands; these are in the same hand as the six illustrations
 * next door — one weight, round caps, colour from `currentColor` — so the band looks like the rest
 * of the page rather than like a component library dropped into it.
 *
 * KEYED BY TITLE, NOT BY POSITION. `BUILDING` is a list in landing-copy.tsx that this file must not
 * touch, and coupling two files by array index means reordering the roadmap silently reassigns every
 * icon. A title that has no mark here renders no tile at all — visibly plain rather than wrong — and
 * landing-copy.test.tsx fails, which is the signal to draw one.
 *
 * Decorative in the strict sense: every glyph repeats what its own card says in words, so all of
 * them are hidden from assistive technology and none carries a label.
 */

const stroke = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function Glyph({ children }: { children: ReactNode }) {
  return (
    <svg className="tenders-glyph" viewBox="0 0 24 24" aria-hidden="true" focusable="false" {...stroke}>
      {children}
    </svg>
  );
}

/** A filled form: the agent that submits alongside you. */
const SUBMITS = (
  <Glyph>
    <rect x="4" y="3" width="16" height="18" rx="2" />
    <path d="M8 8h8M8 12h8M8 16h4" />
  </Glyph>
);

/** A bell: one morning, one mail, only what is relevant. */
const ALERTS = (
  <Glyph>
    <path d="M18 8a6 6 0 1 0-12 0c0 6-2 7-2 7h16s-2-1-2-7Z" />
    <path d="M10.3 20a2 2 0 0 0 3.4 0" />
  </Glyph>
);

/** A lens over ruled lines: reading deeper into the documents themselves. */
const READS = (
  <Glyph>
    <circle cx="11" cy="11" r="6.5" />
    <path d="M15.8 15.8 21 21" />
    <path d="M8.5 10h5M8.5 12.6h3" />
  </Glyph>
);

/** A shield with a check: the contractor classification you are cleared for. */
const CLASSIFIES = (
  <Glyph>
    <path d="M12 3 4 6.5v5c0 5 3.4 8.3 8 9.5 4.6-1.2 8-4.5 8-9.5v-5Z" />
    <path d="m9.2 12.2 2.1 2.1L15 10.6" />
  </Glyph>
);

const GLYPHS: Record<string, ReactNode> = {
  'סוכן שמגיש איתך': SUBMITS,
  'התראות לפי הפרופיל שלך': ALERTS,
  'קריאה עמוקה של המסמכים': READS,
  'סינון לפי סיווג קבלן': CLASSIFIES,
};

/** The mark for one roadmap item, or `null` when none has been drawn for it yet. */
export function buildGlyph(title: string): ReactNode | null {
  return GLYPHS[title] ?? null;
}

/** Every title this file has a mark for — read by the test that keeps the two lists in step. */
export const GLYPH_TITLES = Object.keys(GLYPHS);
